import { useSelector } from "react-redux"

import {
  closeCreateTeeup,
  createTeeup,
  getContacts,
} from "@actions/createTeeupActions"
import { fetchRequests } from "@actions/requestActions"
import { fetchTeeups } from "@actions/teeupActions"
import { autoCoverFor } from "@config/autoCover"
import { selectUserInfo, selectUserId } from "@selectors/user"

import {
  useCreateTeeUpDispatch,
  useCreateTeeUpState,
} from "./CreatePageContext"
import {
  SET_TEEUP_NAME,
  SET_TEEUP_IMAGE,
  SET_TEEUP_MESSAGE,
  SET_INVITEES,
  SET_IS_INVITEES_OPEN,
  APPLY_AUTO_COVER,
} from "./actionTypes"

// A "meaningful" title -- long enough that a topic-aligned cover pick is worth committing to (a
// single stray character shouldn't burn the one-time adjustment).
const MEANINGFUL_TITLE_MIN_LEN = 2

export const useCreateTeeUp = () => {
  // NOTE: this hook is also rendered OUTSIDE a CreateTeeUpProvider (e.g. CreateTeeupInviteScreen
  // reached from an EXISTING TeeUp's People card "Invite" button, which passes its own
  // setInvitees/setInviteToTeeUpById props instead). useCreateTeeUpDispatch()/useCreateTeeUpState()
  // return undefined (not throw) in that case -- default them here instead of crashing on
  // destructure, and no-op any dispatch call so this hook is safe to call from either context.
  const dispatch = useCreateTeeUpDispatch()
  const {
    image,
    name,
    message,
    invitees,
    isInviteesOpen,
    coverManuallySet,
    coverAutoAdjusted,
  } = useCreateTeeUpState() || {}
  const userInfo = useSelector(selectUserInfo)
  const userId = useSelector(selectUserId)

  // Real user pick (the SelectImage screen's real picker) -- always wins, never overridden.
  const setImage = value => {
    dispatch?.({ type: SET_TEEUP_IMAGE, payload: value })
  }

  // CREATE-COVER 2026-07-04: the ONE-TIME topic-aligned auto-adjustment, triggered as the title
  // forms (manual commit-on-blur in TopBar, or the AI rail's patch path). No-ops if the user has
  // already manually chosen a cover, if the adjustment already fired once for this draft, or if
  // the candidate name isn't meaningful yet -- so it is always safe to call speculatively from
  // either trigger site.
  const maybeAutoAdjustCover = candidateName => {
    if (coverManuallySet || coverAutoAdjusted) return
    const trimmed = String(candidateName || "").trim()
    if (trimmed.length < MEANINGFUL_TITLE_MIN_LEN) return
    dispatch?.({ type: APPLY_AUTO_COVER, payload: autoCoverFor(trimmed) })
  }

  const setName = value => {
    dispatch?.({ type: SET_TEEUP_NAME, payload: value })
  }

  const setMessage = value => {
    dispatch?.({ type: SET_TEEUP_MESSAGE, payload: value })
  }

  const setInvitees = value => {
    dispatch?.({ type: SET_INVITEES, payload: value })
  }

  const setIsInviteesOpen = value => {
    dispatch?.({ type: SET_IS_INVITEES_OPEN, payload: value })
  }

  return {
    image,
    name,
    message,
    invitees,
    userInfo,
    isInviteesOpen,
    userId,
    coverManuallySet,
    coverAutoAdjusted,
    closeCreateTeeup,
    createTeeup,
    getContacts,
    fetchTeeups,
    fetchRequests,
    setImage,
    setName,
    setMessage,
    setInvitees,
    setIsInviteesOpen,
    maybeAutoAdjustCover,
  }
}
